import { chakra } from '@chakra-ui/react';
import { useId, useMemo } from 'react';
import { Text } from '../primitives/Text';

/**
 * A compact donut chart for a small categorical mix (§14 dashboard — "runs by status").
 * Dependency-free inline SVG in the {@link CostChart} tradition: a square plot (uniform
 * scale, so text is undistorted) drawn as stacked stroked arcs, with a legend that names
 * every segment and its count + share — the mix never relies on color alone.
 *
 * The track and centre text inherit `currentColor` from a `text.secondary` wrapper so the
 * empty ring and labels are theme-aware; each segment carries an explicit, colourblind-safe
 * hue supplied by the caller.
 */

export interface DonutDatum {
  id: string;
  label: string;
  value: number;
  /** Segment color (colourblind-safe hue). */
  color: string;
}

export interface DonutProps {
  data: DonutDatum[];
  /** Diameter in px. */
  size?: number;
  /** Ring thickness in px. */
  thickness?: number;
  /** Big number in the middle (e.g. total runs). */
  centerValue?: string;
  centerLabel?: string;
  ariaLabel: string;
  'data-testid'?: string;
}

const Wrap = chakra('div', {
  base: {
    display: 'flex',
    gap: '5',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
});

const Legend = chakra('ul', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2',
    listStyle: 'none',
    minWidth: '0',
    flex: '1 1 10rem',
  },
});

const LegendRow = chakra('li', {
  base: { display: 'flex', alignItems: 'center', gap: '2.5', minWidth: 0 },
});

export function Donut({
  data,
  size = 168,
  thickness = 22,
  centerValue,
  centerLabel,
  ariaLabel,
  'data-testid': testid,
}: DonutProps) {
  const titleId = useId();
  const model = useMemo(() => {
    const total = data.reduce((s, d) => s + d.value, 0);
    const r = (size - thickness) / 2;
    const c = 2 * Math.PI * r;
    let offset = 0;
    const segs = data
      .filter((d) => d.value > 0)
      .map((d) => {
        const frac = total > 0 ? d.value / total : 0;
        const seg = { ...d, frac, len: frac * c, dashOffset: -offset * c };
        offset += frac;
        return seg;
      });
    return { total, r, c, segs };
  }, [data, size, thickness]);

  const cx = size / 2;
  const isEmpty = model.total === 0;

  return (
    <Wrap data-testid={testid}>
      <Text as="div" tone="secondary" style={{ flex: '0 0 auto', position: 'relative' }}>
        <svg width={size} height={size} role="img" aria-labelledby={titleId}>
          <title id={titleId}>{ariaLabel}</title>
          {/* Track ring (also the whole ring when empty). */}
          <circle
            cx={cx}
            cy={cx}
            r={model.r}
            fill="none"
            stroke="currentColor"
            strokeOpacity={0.14}
            strokeWidth={thickness}
          />
          {/* Rotate -90° so the first segment starts at 12 o'clock. */}
          <g transform={`rotate(-90 ${cx} ${cx})`}>
            {model.segs.map((s) => (
              <circle
                key={s.id}
                cx={cx}
                cy={cx}
                r={model.r}
                fill="none"
                stroke={s.color}
                strokeWidth={thickness}
                strokeDasharray={`${s.len} ${model.c - s.len}`}
                strokeDashoffset={s.dashOffset}
                strokeLinecap="butt"
              >
                <title>{`${s.label}: ${s.value} (${Math.round(s.frac * 100)}%)`}</title>
              </circle>
            ))}
          </g>
          {centerValue !== undefined && (
            <text
              x={cx}
              y={cx}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={size * 0.2}
              fontWeight={700}
              fill="currentColor"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              <tspan x={cx} dy="-0.15em" fill="currentColor">
                {centerValue}
              </tspan>
              {centerLabel && (
                <tspan x={cx} dy="1.5em" fontSize={size * 0.075} fontWeight={500} fillOpacity={0.75}>
                  {centerLabel}
                </tspan>
              )}
            </text>
          )}
        </svg>
      </Text>

      <Legend>
        {isEmpty ? (
          <Text size="sm" tone="muted">
            No runs in this window yet.
          </Text>
        ) : (
          data
            .filter((d) => d.value > 0)
            .map((d) => (
              <LegendRow key={d.id}>
                <span
                  aria-hidden
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    flexShrink: 0,
                    backgroundColor: d.color,
                  }}
                />
                <Text size="sm" truncate style={{ flex: 1 }}>
                  {d.label}
                </Text>
                <Text as="span" size="sm" tone="secondary" mono>
                  {d.value}
                </Text>
                <Text as="span" size="xs" tone="muted" style={{ width: '2.75rem', textAlign: 'right' }}>
                  {Math.round((d.value / model.total) * 100)}%
                </Text>
              </LegendRow>
            ))
        )}
      </Legend>
    </Wrap>
  );
}
